import {FormControl, FormGroup} from '@angular/forms';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    ClientStatusesEnum,
    HttpStatusCodesEnum,
    TradingAccountStatusesEnum,
    TradingService,
    CompanyExtendedModel,
    IndividualExtendedModel,
    TradingAccountStatusTriggerModel,
    TradingAccountDetailedReportModel,
    CashAccountDetailedReportModel,
    AccountStatusUpdateInterface
} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {RadioGroupOptionInterface} from '../../../../shared/components';
import {BaseControl, Helper, RadioGroupControl} from '../../../../shared/classes';

import {ChangeTradingAccountStatusFormErrorStatesEnum} from '../../enums';

import {
    ChangeTradingAccountStatusControls,
    ChangeTradingAccountStatusOptions,
    ChangeTradingCashAccountStatusOptions,
    ChangeTradingAccountStatusSelector
} from '../../../../../assets/descriptors';

export class ChangeTradingAccountStatusForm {
    private tradingService: TradingService;
    private client: IndividualExtendedModel | CompanyExtendedModel;
    private account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;

    public formGroup: FormGroup;
    public controls: BaseControl[];

    constructor(
        client: IndividualExtendedModel | CompanyExtendedModel,
        account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
        tradingService: TradingService
    ) {
        this.account = account;
        this.client = client;
        this.tradingService = tradingService;
    }

    initForm(triggers: number[]): void {
        this.formGroup = new FormGroup({});

        this.controls = ChangeTradingAccountStatusControls().map((control: BaseControl) => {
            if (control.name === ChangeTradingAccountStatusSelector) {
                control.value = this.account.accountStatus;

                if (!this.account.isCashAccount) {
                    control.extendValidators(InArrayValidator(Helper.enumsHelper().getTradingAccountStatuses()));
                    (control as RadioGroupControl).options = ChangeTradingAccountStatusOptions();
                } else {
                    control.extendValidators(InArrayValidator(Helper.enumsHelper().getCashAccountStatuses()));
                    (control as RadioGroupControl).options = ChangeTradingCashAccountStatusOptions();
                }

                (control as RadioGroupControl).options.map((option: RadioGroupOptionInterface) => {
                    if ((triggers.indexOf(option.value as number) < 0) && (option.value !== this.account.accountStatus)) {
                        option.disabled = true;
                    }
                });
            }

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });
    }

    validateForm(
        messagesFunction: (controlLabel?: string) => { [key: string]: string },
        differentStatusValidationKey: string
    ): boolean {
        return this.controls.map((control: BaseControl) => {
            const formControl = this.formGroup.controls[control.name];

            if (formControl && formControl.invalid) {
                control.errors = Object.keys(formControl.errors).map((key: string) => {
                    return messagesFunction(control.placeholder)[key];
                });

                return false;
            }

            const accountStatus = this.account.accountStatus;

            if ((control.name === ChangeTradingAccountStatusSelector) && (accountStatus === control.value)) {
                control.setError(messagesFunction()[differentStatusValidationKey]);

                return false;
            }

            return true;
        }).indexOf(false) < 0;
    }

    createRequest(updateToClosedPermissions: string[], authUserPermissions: string[]): Observable<ApiError | boolean> {
        const currentStatus = this.account.accountStatus;
        const newStatus = this.formGroup.value[ChangeTradingAccountStatusSelector];
        const clientStatus = !this.account.isOwnerCompany ? (this.client as IndividualExtendedModel).individualStatus :
            (this.client as CompanyExtendedModel).status;

        if (
            (newStatus === TradingAccountStatusesEnum.Closed) &&
            !(Helper.getUnionStrategyPermissionsCheck(updateToClosedPermissions, authUserPermissions))
        ) {
            const error = new ApiError(
                ChangeTradingAccountStatusFormErrorStatesEnum.UpdateTradingAccountStatusToClosedRestricted
            );

            return of(error);
        }

        if (
            (currentStatus === TradingAccountStatusesEnum.ForApproval) &&
            (newStatus === TradingAccountStatusesEnum.Active) &&
            (clientStatus !== ClientStatusesEnum.Active)
        ) {
            const error = new ApiError(ChangeTradingAccountStatusFormErrorStatesEnum.NotActiveAccountOwner);

            return of(error);
        }

        if (this.account.accountStatus !== newStatus) {
            const id = this.account.accountId;
            const model = new TradingAccountStatusTriggerModel(id, newStatus);

            if (this.account.isCashAccount)
            {
                return this.tradingService.updateCashAccountStatus(model).pipe(
                    map((response: ApiResponse<ApiError[] | AccountStatusUpdateInterface>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return new ApiError(
                                ChangeTradingAccountStatusFormErrorStatesEnum.UpdateTradingAccountStatusFailed
                            );
                        }
    
                        var result = response.data as AccountStatusUpdateInterface;
                        this.account.accountStatus = result.new_status;
    
                        return true;
                    })
                );
            }
            else
            {
                return this.tradingService.updateAccountStatus(model).pipe(
                    map((response: ApiResponse<ApiError[] | AccountStatusUpdateInterface>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return new ApiError(
                                ChangeTradingAccountStatusFormErrorStatesEnum.UpdateTradingAccountStatusFailed
                            );
                        }
    
                        var result = response.data as AccountStatusUpdateInterface;
                        this.account.accountStatus = result.new_status;
    
                        return true;
                    })
                );
            }
        }

        return of(true);
    }
}
